import { Component } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Button from '@mui/material/Button';
import analytics from 'modules/analytics';
import { env } from 'config/env';
import localforage from 'localforage';

type ErrorBoundaryProps = {
  name: string;
  children: React.ReactNode;
};

type ErrorBoundaryState = {
  isDialogOpen: boolean;
  error: Nullable<Error>;
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    isDialogOpen: false,
    error: null,
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return {
      isDialogOpen: true,
      error: error,
    };
  }

  componentDidCatch(
    error: Error,
    errorInfo: {
      componentStack: string;
    }
  ) {
    analytics.error(error, {
      componentStack: errorInfo.componentStack,
      boundaryName: this.props.name,
    });

    // If a React error reaches this boundary, let's clear the
    // persistent Apollo cache so that we can start fresh when
    // the user refreshes the page.
    localforage.clear();
  }

  handleClose: () => void = () => {
    this.setState({
      isDialogOpen: false,
      error: null,
    });
  };

  render(): React.ReactNode {
    const { isDialogOpen, error } = this.state;
    if (isDialogOpen) {
      return (
        <>
          <Dialog open={isDialogOpen} data-testid="error-boundary-dialog">
            <DialogTitle>An Error Occurred</DialogTitle>
            <DialogContent>
              <DialogContentText data-testid="error-boundary-message">
                {error?.message}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={this.handleClose} color="primary" autoFocus>
                Ok
              </Button>
            </DialogActions>
          </Dialog>
        </>
      );
    }

    return this.props.children;
  }
}

const ConditionalErrorBoundary: React.ComponentType<ErrorBoundaryProps> = ({ name, children }) =>
  env.NODE_ENV === 'development' ? children : <ErrorBoundary name={name}>{children}</ErrorBoundary>;

export default ConditionalErrorBoundary;
